import { effect, inject } from '@angular/core';
import { FileService, Link, LinksService, UploadFile200Response } from '@api';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { pipe, switchMap, tap } from 'rxjs';
import { UserStore } from './user.store';

export interface LinkStateInterface {
  links: Link[];
  total: number;
  searchFilter: string;
  colSort: string;
  sortOrder: 'asc' | 'desc' | '';
  page: number;
  pageSize: number;
  loadingLinks: boolean;
}

export const LinksStore = signalStore(
  withState<LinkStateInterface>({
    links: [],
    total: 0,
    searchFilter: '',
    colSort: 'name',
    sortOrder: 'asc',
    page: 1,
    pageSize: 10,
    loadingLinks: false,
  }),
  withMethods(
    (
      store,
      userStore = inject(UserStore),
      linksService = inject(LinksService),
      toastr = inject(ToastrService),
      fileService = inject(FileService)
    ) => ({
      addLink(link: Link, file: Blob) {
        fileService
          .uploadImage(file)
          .subscribe((resp: UploadFile200Response) => {
            link.uuid = resp.uuid;
            linksService
              .saveLink(link)
              .subscribe(
                () =>
                  toastr.success(`Le lien "${link.name}" a été créé`) &&
                  this.loadLinks()
              );
          });
      },
      updateLink(link: Link, file: Blob) {
        if (file) {
          fileService
            .uploadImage(file)
            .subscribe((resp: UploadFile200Response) => {
              link.uuid = resp.uuid;
              linksService
                .updateLink(link.id, link)
                .subscribe(
                  () =>
                    toastr.success(`Le lien "${link.name}" a été mis à jour`) &&
                    this.loadLinks()
                );
            });
        } else {
          linksService
            .updateLink(link.id, link)
            .subscribe(
              () =>
                toastr.success(`Le lien "${link.name}" a été mis à jour`) &&
                this.loadLinks()
            );
        }
      },
      removeLink(link: Link) {
        linksService
          .deleteLink(link.id)
          .subscribe(
            () =>
              toastr.success(`Le lien "${link.name}" a été supprimé`) &&
              this.loadLinks()
          );
      },
      pageEvent(
        colSort: string,
        sortOrder: 'asc' | 'desc' | '',
        page: number,
        pageSize: number
      ) {
        patchState(store, { colSort, sortOrder, page, pageSize });
        this.loadLinks();
      },
      loadLinks: rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, { loadingLinks: true });
            return linksService
              .getLinks(
                userStore.contract()?.id,
                store.colSort(),
                store.sortOrder(),
                store.page(),
                store.pageSize(),
                store.searchFilter()
              )
              .pipe(
                tap((page) => {
                  patchState(store, {
                    // @ts-ignore
                    links: page.results,
                    total: page.total,
                    loadingLinks: false,
                  });
                })
              );
          })
        )
      ),
    })
  ),
  withHooks({
    onInit(store) {
      store.loadLinks();

      const userStore = inject(UserStore);
      effect(
        () => {
          const contract = userStore.contract();
          store.loadLinks();
        },
        { allowSignalWrites: true }
      );
    },
  })
);
